import { CollectionRef } from "@/types/collection"
import { InspirationRef, PlaceRef } from "@/types/entity"
import { TripRef } from "@/types/trip"

export type TripSyncMessageType =
  | "PLACE_ADDED_TO_TRIP"
  | "PLACE_REMOVED_FROM_TRIP"
  | "TRIP_CREATED"
  | "TRIP_DELETED"
  | "TRIP_REFS_INVALIDATED"
  | "MEDIA_TRIP_REFS_INVALIDATED"

export interface TripSyncMessage {
  type: TripSyncMessageType
  placeRef?: PlaceRef
  tripRef?: TripRef
  placeRefs?: PlaceRef[]
  mediaRefs?: (InspirationRef | CollectionRef)[]
}

export class TripSync {
  private static _instance: TripSync | undefined
  private channel: BroadcastChannel | undefined
  private listeners: ((message: TripSyncMessage) => void)[] = []

  static get instance(): TripSync {
    if (!TripSync._instance) {
      TripSync._instance = new TripSync()
    }
    return TripSync._instance
  }

  private constructor() {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
      return
    }

    try {
      this.channel = new BroadcastChannel("mindtrip-trip-sync")
    } catch (error) {
      // SecurityError marks denied storage access; NotSupportedError marks an
      // opaque origin in Chromium-based clients. Cross-tab sync is optional, so
      // both use the existing no-channel fallback.
      if (
        error instanceof DOMException &&
        (error.name === "SecurityError" || error.name === "NotSupportedError")
      ) {
        return
      }

      throw error
    }

    this.channel.addEventListener("message", this.handleMessage.bind(this))
  }

  private handleMessage(event: MessageEvent<TripSyncMessage>) {
    this.listeners.forEach((listener) => {
      try {
        listener(event.data)
      } catch (error) {
        console.error("Error in TripSync listener:", error)
      }
    })
  }

  broadcastPlaceAddedToTrip(placeRef: PlaceRef, tripRef: TripRef) {
    if (!this.channel) {
      return
    }

    const message: TripSyncMessage = {
      type: "PLACE_ADDED_TO_TRIP",
      placeRef,
      tripRef,
    }

    this.channel.postMessage(message)
  }

  broadcastPlaceRemovedFromTrip(placeRef: PlaceRef, tripRef: TripRef) {
    if (!this.channel) {
      return
    }

    const message: TripSyncMessage = {
      type: "PLACE_REMOVED_FROM_TRIP",
      placeRef,
      tripRef,
    }

    this.channel.postMessage(message)
  }

  broadcastTripCreated(tripRef: TripRef) {
    if (!this.channel) {
      return
    }

    const message: TripSyncMessage = {
      type: "TRIP_CREATED",
      tripRef,
    }

    this.channel.postMessage(message)
  }

  broadcastTripDeleted(tripRef: TripRef) {
    if (!this.channel) {
      return
    }

    const message: TripSyncMessage = {
      type: "TRIP_DELETED",
      tripRef,
    }

    this.channel.postMessage(message)
  }

  broadcastTripRefsInvalidated(placeRefs: PlaceRef[]) {
    if (!this.channel) {
      return
    }

    const message: TripSyncMessage = {
      type: "TRIP_REFS_INVALIDATED",
      placeRefs,
    }

    this.channel.postMessage(message)
  }

  broadcastMediaTripRefsInvalidated(
    mediaRefs: (InspirationRef | CollectionRef)[]
  ) {
    if (!this.channel) {
      return
    }

    const message: TripSyncMessage = {
      type: "MEDIA_TRIP_REFS_INVALIDATED",
      mediaRefs,
    }

    this.channel.postMessage(message)
  }

  addListener(listener: (message: TripSyncMessage) => void) {
    this.listeners.push(listener)
  }

  removeListener(listener: (message: TripSyncMessage) => void) {
    const index = this.listeners.indexOf(listener)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  destroy() {
    if (this.channel) {
      this.channel.close()
      this.channel = undefined
    }
    this.listeners = []
  }
}
